import { useRef, useState } from "react";
import {
  View,
  Text,
  Pressable,
  Modal,
  Platform,
  Dimensions,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { MoreHorizontal, type LucideIcon } from "lucide-react-native";
import { colors } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type KebabMenuItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  onPress: () => void;
  tone?: "default" | "destructive";
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
};

type Props = {
  items: KebabMenuItem[];
  /** Color of the kebab dots. Override on dark surfaces. */
  buttonColor?: string;
  accessibilityLabel?: string;
  /** Override the trigger glyph (defaults to the kebab dots). */
  triggerIcon?: LucideIcon;
  /** Size of the trigger glyph. */
  triggerIconSize?: number;
  /** Extra style merged onto the trigger button (e.g. a pill background). */
  triggerStyle?: StyleProp<ViewStyle>;
};

const MENU_WIDTH = 220;
const DESTRUCTIVE = "#9A4C32";

export function KebabMenu({
  items,
  buttonColor = colors.textMid,
  accessibilityLabel = "Open menu",
  triggerIcon: TriggerIcon = MoreHorizontal,
  triggerIconSize = 21,
  triggerStyle,
}: Props) {
  const buttonRef = useRef<View>(null);
  const capturePress = usePressCapture("KebabMenu");
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(
    null,
  );

  const open = () => {
    capturePress("kebab_menu_trigger");
    buttonRef.current?.measureInWindow((x, y, w, h) => {
      const screenWidth = Dimensions.get("window").width;
      setAnchor({
        top: y + h + 6,
        right: Math.max(8, screenWidth - (x + w)),
      });
    });
  };

  const close = () => setAnchor(null);

  return (
    <>
      <Pressable
        ref={buttonRef}
        onPress={open}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        style={[
          {
            width: 32,
            height: 32,
            borderRadius: 16,
            alignItems: "center",
            justifyContent: "center",
          },
          triggerStyle,
        ]}
      >
        <TriggerIcon
          size={triggerIconSize}
          color={buttonColor}
          strokeWidth={2}
        />
      </Pressable>
      <Modal
        visible={anchor !== null}
        transparent
        animationType="fade"
        onRequestClose={close}
      >
        <Pressable onPress={close} style={{ flex: 1 }}>
          {anchor && (
            <Pressable
              onPress={(e) => e.stopPropagation?.()}
              style={{
                position: "absolute",
                top: anchor.top,
                right: anchor.right,
                width: MENU_WIDTH,
                borderRadius: 12,
                backgroundColor:
                  Platform.OS === "web"
                    ? "rgba(255,255,255,0.92)"
                    : colors.surface,
                borderWidth: 1,
                borderColor: "rgba(0,0,0,0.08)",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.12,
                shadowRadius: 24,
                ...(Platform.OS === "web"
                  ? ({
                      backdropFilter: "blur(20px)",
                      WebkitBackdropFilter: "blur(20px)",
                    } as Record<string, string>)
                  : {}),
                overflow: "hidden",
              }}
            >
              {items.map((item, i) => {
                const Icon = item.icon;
                const tint =
                  item.tone === "destructive" ? DESTRUCTIVE : colors.text;
                const iconTint =
                  item.tone === "destructive" ? DESTRUCTIVE : colors.textMid;
                return (
                  <Pressable
                    key={item.id}
                    onPress={() => {
                      capturePress(
                        pressLabel(item.label),
                        item.action ?? item.id,
                      );
                      close();
                      item.onPress();
                    }}
                    style={({ pressed }) => ({
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 10,
                      paddingVertical: 11,
                      paddingHorizontal: 14,
                      backgroundColor: pressed
                        ? "rgba(0,0,0,0.04)"
                        : "transparent",
                      borderTopWidth: i === 0 ? 0 : 1,
                      borderTopColor: "rgba(0,0,0,0.06)",
                    })}
                  >
                    <Icon size={16} color={iconTint} strokeWidth={1.7} />
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: "500",
                        color: tint,
                      }}
                    >
                      {item.label}
                    </Text>
                  </Pressable>
                );
              })}
            </Pressable>
          )}
        </Pressable>
      </Modal>
    </>
  );
}
